import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { acAPI } from "../../lib/academicApi";
import { PageHeader, Loading, Section, StatusPill, EmptyState } from "../../components/Common";
import { ArrowLeft, Plus, BookOpen } from "lucide-react";
import { toast } from "sonner";

export default function Homework360() {
  const { school } = useParams();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", due_at: "", max_score: 20 });
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selClass, setSelClass] = useState("");
  const [selSubject, setSelSubject] = useState("");

  useEffect(() => {
    (async () => {
      const [c, s] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/education360/classes`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("edu360.token")}` }
        }).then(r => r.json()),
        acAPI.subjects().then(r => r.data),
      ]);
      setClasses(c || []); setSubjects(s || []);
    })();
  }, []);

  const load = () => acAPI.homework().then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!selClass || !selSubject || !form.title || !form.due_at) return toast.error("Fill all required fields");
    try {
      await acAPI.createHomework({ ...form, class_id: selClass, subject_id: selSubject });
      toast.success("Homework published");
      setForm({ title: "", description: "", due_at: "", max_score: 20 });
      load();
    } catch { toast.error("Failed"); }
  };

  if (items === null) return <Loading />;

  return (
    <div data-testid="homework360">
      <PageHeader
        kicker="Academic360™ · Homework360"
        title="Set, track, mark"
        subtitle="Publish homework across classes. AI summaries help teachers batch-mark."
        actions={<button onClick={() => nav(`/education360/${school}/academic`)} className="hl-button-secondary text-xs"><ArrowLeft className="w-3 h-3 inline mr-1" />Hub</button>}
      />

      <Section title="Publish new homework">
        <div className="hl-card p-3 grid grid-cols-1 md:grid-cols-6 gap-2 text-sm items-end" data-testid="new-form">
          <select value={selClass} onChange={e => setSelClass(e.target.value)} className="border border-slate-200 rounded px-2 py-2" data-testid="new-class">
            <option value="">Class</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={selSubject} onChange={e => setSelSubject(e.target.value)} className="border border-slate-200 rounded px-2 py-2" data-testid="new-subject">
            <option value="">Subject</option>
            {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input placeholder="Title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="border border-slate-200 rounded px-2 py-2 md:col-span-2" data-testid="new-title" />
          <input type="datetime-local" value={form.due_at} onChange={e => setForm({ ...form, due_at: e.target.value })} className="border border-slate-200 rounded px-2 py-2" data-testid="new-due" />
          <button onClick={create} className="hl-button-primary text-xs" data-testid="btn-create-hw"><Plus className="w-3 h-3 inline mr-1" />Publish</button>
        </div>
      </Section>

      <Section title={`Homework (${items.length})`}>
        <div className="hl-card overflow-hidden">
          <table className="hl-table" data-testid="hw-table">
            <thead><tr><th>Title</th><th>Subject</th><th>Class</th><th>Due</th><th>Max</th><th>Status</th></tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-500">No homework yet.</td></tr>}
              {items.map(h => {
                const cls = classes.find(c => c.id === h.class_id);
                const subj = subjects.find(s => s.id === h.subject_id);
                return (
                  <tr key={h.id}>
                    <td><b>{h.title}</b>{h.description && <div className="text-xs text-slate-500 truncate max-w-[280px]">{h.description}</div>}</td>
                    <td>{subj?.name || "—"}</td>
                    <td>{cls?.name || "—"}</td>
                    <td className="mono text-xs">{h.due_at?.slice(0, 10)}</td>
                    <td>{h.max_score}</td>
                    <td><StatusPill status={h.status} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
